import axios from "axios";

const APP_NAME = "susie_agent";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8000",
  timeout: 60000,
});

// Phrases Susie uses when she can't answer
const NO_ANSWER_PHRASES = [
  "i don't have that information",
  "don't have that information in my shell",
  "i'll let the serc team know",
  "i'm really just an expert on scaly",
  "you might want to check with a different source",
  "i don't have enough information",
  "my notes don't cover",
];

function wasAnswered(text) {
  const lower = text.toLowerCase();
  return !NO_ANSWER_PHRASES.some((phrase) => lower.includes(phrase));
}

/**
 * Create an ADK session for a user.
 * Returns the ADK session id.
 */
export async function createSession(userId) {
  const { data } = await api.post(
    `/apps/${APP_NAME}/users/${userId}/sessions`,
    { state: {} }
  );
  return data.id;
}

/**
 * Extract source documents from ADK response events.
 * Handles both Vertex AI Search (GCS URIs → title only) and
 * Google Search (HTTP URIs → clickable links).
 */
function extractSources(events) {
  const seen = new Set();
  const sources = [];

  for (const event of events) {
    // Grounding metadata (populated by both VertexAiSearchTool and google_search)
    const chunks = event?.grounding_metadata?.grounding_chunks || [];
    for (const chunk of chunks) {
      const ctx = chunk?.retrieved_context;
      const web = chunk?.web;
      const title = ctx?.title || web?.title;
      const uri = ctx?.uri || web?.uri;
      if (title && !seen.has(title)) {
        seen.add(title);
        // Only expose URI if it's a public HTTP link (not a gs:// GCS URI)
        const publicUri = uri?.startsWith("http") ? uri : null;
        sources.push({ title, uri: publicUri });
      }
    }

    // ADK tool response parts (fallback if grounding_metadata isn't populated)
    const parts = event?.content?.parts || [];
    for (const part of parts) {
      const results =
        part?.functionResponse?.response?.results ||
        part?.functionResponse?.response?.search_results ||
        [];
      for (const result of results) {
        const title =
          result?.document?.derived_struct_data?.title ||
          result?.title;
        if (title && !seen.has(title)) {
          seen.add(title);
          sources.push({ title, uri: null });
        }
      }
    }
  }

  return sources.slice(0, 3);
}

/**
 * Send a message to Susie via ADK /run endpoint.
 * Returns { response, gapId, wasAnswered, sources }.
 */
export async function sendMessage(userId, sessionId, message) {
  const { data: events } = await api.post("/run", {
    app_name: APP_NAME,
    user_id: userId,
    session_id: sessionId,
    new_message: {
      role: "user",
      parts: [{ text: message }],
    },
  });

  // Find the final text response from the agent events
  let responseText = "";
  for (const event of events) {
    const parts = event?.content?.parts;
    if (parts) {
      const textParts = parts.filter((p) => p.text).map((p) => p.text);
      if (textParts.length > 0) {
        responseText = textParts.join("");
      }
    }
  }

  if (!responseText) {
    responseText =
      "Oh my shell, something went wrong on my end! Please try asking again in a moment.";
  }

  const answered = wasAnswered(responseText);
  const sources = extractSources(events);

  // Log the knowledge gap in the background
  let gapId = null;
  try {
    const { data: gapData } = await api.post("/api/track-gap", {
      session_id: sessionId,
      question: message,
      response: responseText,
      was_answered: answered,
    });
    gapId = gapData.gap_id;
  } catch (err) {
    console.warn("Gap tracking failed:", err);
  }

  return { response: responseText, gapId, wasAnswered: answered, sources };
}

export async function submitFeedback(messageId, feedback, notes = null) {
  const { data } = await api.post("/api/feedback", {
    message_id: messageId,
    feedback,
    notes,
  });
  return data;
}

export async function getGaps({
  reviewed,
  flagged,
  wasAnswered,
  limit = 50,
  offset = 0,
} = {}) {
  const params = { limit, offset };
  if (reviewed !== undefined) params.reviewed = reviewed;
  if (flagged !== undefined) params.flagged = flagged;
  if (wasAnswered !== undefined) params.was_answered = wasAnswered;
  const { data } = await api.get("/api/gaps", { params });
  return data;
}

export async function updateGap(gapId, updates) {
  const { data } = await api.patch(`/api/gaps/${gapId}`, updates);
  return data;
}
